"use client";

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Send, Swords } from "lucide-react";

import { Button } from "@acme/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@acme/ui/dialog";
import { Input } from "@acme/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@acme/ui/tabs";

import { api } from "~/common/utils/trpc.js";
import {
  CwNotificationDevTestTab,
  useCwDevTestRunner,
} from "~/features/clan-war-notifications/_components/cw-notification-dev-test-tab.js";
import { MY_PLAYER_TAG, FAKE_CLAN_TAG } from "~/features/clan-war-notifications/_dev/fake-cw-war.js";
import type { ConnectionStatus, CwNotificationType } from "@acme/db/schema";

/** Nur gemountet wenn CW-Tab aktiv, damit geschützte tRPC-Calls erst nach Login laufen. */
function CwNotificationSimulationPanel({
  selectedTypes,
  onSelectedTypesChange,
  myAttacksLeft,
  onMyAttacksLeftChange,
  simulatedConnectionStatus,
  onSimulatedConnectionStatusChange,
}: {
  readonly selectedTypes: Set<CwNotificationType>;
  readonly onSelectedTypesChange: (next: Set<CwNotificationType>) => void;
  readonly myAttacksLeft: number;
  readonly onMyAttacksLeftChange: (n: number) => void;
  readonly simulatedConnectionStatus: ConnectionStatus;
  readonly onSimulatedConnectionStatusChange: (s: ConnectionStatus) => void;
})
{
  const { runTestSelected, runTestAll, isRunning, progressTotalMs, progressElapsedMs, displaySimulatedRemainingMs } =
    useCwDevTestRunner({
      playerTag: MY_PLAYER_TAG,
      clanTag: FAKE_CLAN_TAG,
      connectionStatus: simulatedConnectionStatus,
      selectedTypes,
      myAttacksLeft,
    });

  return (
    <>
      <p className="mb-4 text-sm text-muted-foreground">
        Reine Simulation mit Fake-Clan (#C2R2P2Q2L) und Fake-Player (#P2Q8Y9L). Kein echter Clan-Kontext.
      </p>
      <CwNotificationDevTestTab
        playerTag={MY_PLAYER_TAG}
        clanTag={FAKE_CLAN_TAG}
        simulatedConnectionStatus={simulatedConnectionStatus}
        onSimulatedConnectionStatusChange={onSimulatedConnectionStatusChange}
        selectedTypes={selectedTypes}
        onSelectedTypesChange={onSelectedTypesChange}
        myAttacksLeft={myAttacksLeft}
        onMyAttacksLeftChange={onMyAttacksLeftChange}
        onRunTestSelected={runTestSelected}
        onRunTestAll={runTestAll}
        isRunning={isRunning}
        progressTotalMs={progressTotalMs}
        progressElapsedMs={progressElapsedMs}
        displaySimulatedRemainingMs={displaySimulatedRemainingMs}
      />
    </>
  );
}

interface TrpcTestingDialogProps
{
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

function StatRow({
  label,
  value,
  indent,
}: {
  readonly label: string;
  readonly value: number;
  readonly indent?: boolean;
})
{
  return (
    <div
      className={`flex items-center justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0 ${indent ? "pl-4" : ""}`}>
      <span className={indent ? "text-xs text-muted-foreground" : "text-muted-foreground"}>{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </div>
  );
}

export function TrpcTestingDialog({ open, onOpenChange }: TrpcTestingDialogProps)
{
  const [activeTab, setActiveTab] = useState("stats");
  const [clanTagForCw, setClanTagForCw] = useState("");
  const [cwDevSelectedTypes, setCwDevSelectedTypes] = useState<Set<CwNotificationType>>(new Set());
  const [cwDevMyAttacksLeft, setCwDevMyAttacksLeft] = useState(2);
  const [cwDevSimulatedConnectionStatus, setCwDevSimulatedConnectionStatus] =
    useState<ConnectionStatus>("connection_requested");

  const queryClient = useQueryClient();
  const statsQueryOptions = api.dev.getStats.queryOptions();
  const {
    data: stats,
    isLoading: isStatsLoading,
    refetch,
  } = useQuery({
    ...statsQueryOptions,
    enabled: open,
  });

  const syncMutation = useMutation({
    ...api.clanWar.triggerSyncAllClansAndPlayers.mutationOptions(),
    onSuccess: () =>
    {
      void queryClient.invalidateQueries({ queryKey: statsQueryOptions.queryKey });
    },
  });
  const cwCheckMutation = useMutation(api.clanWar.triggerCwCheck.mutationOptions());
  const testNotificationMutation = useMutation(api.player.sendTestNotification.mutationOptions());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl! overflow-y-auto w-[95vw]">
        <DialogHeader>
          <DialogTitle>tRPC Testing</DialogTitle>
          <DialogDescription>DB-Stats und Test-Endpoints (nur für Entwicklung).</DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="stats">Stats &amp; Actions</TabsTrigger>
            <TabsTrigger value="cw">CW Notifications (Simulation)</TabsTrigger>
          </TabsList>
          <TabsContent value="stats" className="mt-4" forceMount hidden={activeTab !== "stats"}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:divide-x md:divide-border">
              <section className="space-y-4 md:pr-6">
                <h3 className="text-sm font-semibold text-muted-foreground">DB-Stats</h3>
                {isStatsLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Lade…</span>
                  </div>
                ) : stats ? (
                  <div className="flex flex-col">
                    <StatRow label="Users" value={stats.userCount} />
                    {stats.providerCounts.map(({ providerId, count }) => (
                      <StatRow key={providerId} label={providerId} value={count} indent />
                    ))}
                    <StatRow label="aktive Sessions" value={stats.activeSessionCount} indent />
                    <StatRow label="Players" value={stats.playerCount} />
                    <StatRow label="indexed by users" value={stats.firstIndexerByUser} indent />
                    <StatRow label="indexed by clans" value={stats.firstIndexerByClan} indent />
                    <StatRow label="Clans" value={stats.clanCount} />
                    <StatRow label="mit Owner" value={stats.ownedClanCount} indent />
                  </div>
                ) : null}
                <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isStatsLoading}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Stats neu laden
                </Button>
              </section>

              <section className="space-y-4 md:pl-6">
                <h3 className="text-sm font-semibold text-muted-foreground">Test-Actions</h3>

                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">Sync (Clans + Player)</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => syncMutation.mutate(undefined)}
                      disabled={syncMutation.isPending}>
                      {syncMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Run syncAllClansAndPlayers
                    </Button>
                  </div>

                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">CW Check (Clan-Tag)</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        placeholder="#ClanTag"
                        value={clanTagForCw}
                        onChange={(e) => setClanTagForCw(e.target.value)}
                        className="w-32 font-mono text-sm"
                      />
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          clanTagForCw.trim() ? cwCheckMutation.mutate({ clanTag: clanTagForCw.trim() }) : undefined
                        }
                        disabled={cwCheckMutation.isPending || !clanTagForCw.trim()}>
                        {cwCheckMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        <Swords className="mr-2 h-4 w-4" />
                        Trigger CW Check
                      </Button>
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs text-muted-foreground">Push (an dich)</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => testNotificationMutation.mutate(undefined)}
                      disabled={testNotificationMutation.isPending}>
                      {testNotificationMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      <Send className="mr-2 h-4 w-4" />
                      Test-Push senden
                    </Button>
                  </div>
                </div>
              </section>
            </div>
          </TabsContent>
          <TabsContent value="cw" className="mt-4" forceMount hidden={activeTab !== "cw"}>
            {activeTab === "cw" ? (
              <CwNotificationSimulationPanel
                selectedTypes={cwDevSelectedTypes}
                onSelectedTypesChange={setCwDevSelectedTypes}
                myAttacksLeft={cwDevMyAttacksLeft}
                onMyAttacksLeftChange={setCwDevMyAttacksLeft}
                simulatedConnectionStatus={cwDevSimulatedConnectionStatus}
                onSimulatedConnectionStatusChange={setCwDevSimulatedConnectionStatus}
              />
            ) : null}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
